import { useCallback, useEffect, useState } from "react";
import { Button, Chip, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";

/**
 * SCHEDULED PRICE CHANGES
 *
 * Everything a published price version has been told to do, and has not done
 * yet: one row per subscription, the version it is on, the version it is going
 * to, and the date that happens. The date is the server's, computed once when
 * the admin decided, as max(next renewal, price lock expiry, now) — so a row
 * whose "effective" date is later than its renewal is a customer whose price
 * lock has not run out yet, and that is exactly what the lock is for.
 *
 * Cancel takes a scheduled change off. It never un-does one that has already
 * landed, and it never touches the version itself: new customers keep buying
 * the new price either way.
 *
 * Renders nothing at all when the phase-4 migration has not run.
 */
export default function ScheduledPriceChanges({ priceId, skuId, compact }) {
  const [rows, setRows] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    if (!priceId && !skuId) return;
    setLoading(true);
    try {
      const q = priceId ? `priceId=${priceId}` : `skuId=${skuId}`;
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/catalog/price-rollouts?${q}`,
        { credentials: "include" },
      );
      const data = await res.json();
      setEnabled(!!data?.enabled);
      setRows(data?.data || []);
    } catch (e) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [priceId, skuId]);

  useEffect(() => {
    load();
  }, [load]);

  const cancel = async (id) => {
    setBusyId(id);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_SERVER_URL}/admin/catalog/price-rollouts/${id}/cancel`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({}),
        },
      );
      const data = await res.json();
      if (data?.success) {
        toast.success("Scheduled price change cancelled");
        load();
      } else {
        toast.error(data?.msg || "Could not cancel");
      }
    } catch (e) {
      toast.error("Could not cancel");
    } finally {
      setBusyId(null);
    }
  };

  if (!enabled || (!loading && !rows.length)) return null;

  const fmt = (v) => (v ? new Date(v).toLocaleDateString() : "—");

  return (
    <div style={{ marginTop: compact ? 8 : 16 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <strong style={{ fontSize: 13 }}>Scheduled price changes</strong>
        <Chip size="small" variant="outlined" label={rows.length} />
        {loading && <CircularProgress size={14} />}
      </div>
      <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse", marginTop: 6 }}>
        <thead>
          <tr style={{ textAlign: "left", color: "#666" }}>
            <th>Subscription</th>
            <th>Customer</th>
            <th>Current version</th>
            <th>Target version</th>
            <th>Effective</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.id} style={{ borderTop: "1px solid #eee" }}>
              <td>#{r.subscription_id}</td>
              <td>{r.plan || r.product || `workspace #${r.work_id ?? "?"}`}</td>
              <td>{r.current_version_no ? `v${r.current_version_no}` : "—"}</td>
              <td>{r.target_version_no ? `v${r.target_version_no}` : "—"}</td>
              <td>
                {fmt(r.effective_at)}
                {r.locked_until &&
                new Date(r.locked_until) > new Date(r.next_renewal || 0) ? (
                  <Chip
                    size="small"
                    variant="outlined"
                    style={{ marginLeft: 6 }}
                    label={`price locked to ${fmt(r.locked_until)}`}
                  />
                ) : null}
              </td>
              <td style={{ textAlign: "right" }}>
                <Button
                  size="small"
                  disabled={busyId === r.id}
                  onClick={() => cancel(r.id)}
                >
                  Cancel
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
